import {
  combineLatestTyped,
  IList,
  mergeTyped,
  useDataStream,
  useEventAsStream,
  useStreamValue
} from 'annotation-tools/utils';
import { Range } from 'immutable';
import { useState } from 'react';
import { Observable } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  mapTo,
  startWith
} from 'rxjs/operators';
import {
  paginationDebounceTime,
  searchDebounceTime
} from '../../../constants/debounce-time';
import { rowsPerPageDefault } from '../../../constants/rows-per-page-options';
import { hasSearchString } from '../../../functions/has-search-string';
import {
  ListWithPromiseState,
  listWithPromiseStateInit
} from '../../../global-state/database/common/list-with-status';
import { mapListLatest } from '../../../global-state/database/common/map-latest';
import { ProjectStatus } from '../../../types/enum/project-status';
import { IProjectType } from '../../../types/record/project';
import { useValueAsDataStream } from '../../../utils/use-value-as-data-stream';

export const useProjectListFiltered = (
  projectList$: Observable<ListWithPromiseState<IProjectType>>
) => {
  const [searchString, onSearchStringChange] = useState<string>('');
  const searchString$ = useValueAsDataStream(searchString);

  const [status, setStatus] = useState<ProjectStatus | undefined>();
  const status$ = useValueAsDataStream(status);

  const [author, setAuthor] = useState<string | undefined>();
  const author$ = useValueAsDataStream(author);

  const [approver, setApprover] = useState<string | undefined>();
  const approver$ = useValueAsDataStream(approver);

  const [pageChange$, onPageChange] = useEventAsStream<number>();

  const [rowsPerPage, setRowsPerPage] = useState<number>(rowsPerPageDefault);
  const rowsPerPage$ = useValueAsDataStream(rowsPerPage);

  const projectListFiltered$ = useDataStream<
    ListWithPromiseState<IProjectType>
  >(
    listWithPromiseStateInit<IProjectType>(),
    combineLatestTyped(
      projectList$,
      searchString$.pipe(debounceTime(searchDebounceTime)),
      status$,
      author$,
      approver$
    ).pipe(
      map(([list, searchStr, _status, _author, _approver]) =>
        mapListLatest(list, ls =>
          ls.filter(
            a =>
              (_status === undefined || a.status === _status) &&
              (_author === undefined || a.author === _author) &&
              (_approver === undefined || a.approver === _approver) &&
              hasSearchString(a.name, searchStr)
          )
        )
      )
    )
  );

  const projectListPaginated$ = useDataStream<
    ListWithPromiseState<IList<IProjectType>>
  >(
    listWithPromiseStateInit<IList<IProjectType>>(),
    combineLatestTyped(projectListFiltered$, rowsPerPage$).pipe(
      map(([list, _rowsPerPage]) =>
        mapListLatest(list, ls =>
          Range(0, Math.ceil(ls.size / _rowsPerPage))
            .map(pageIdx =>
              ls.slice(pageIdx * _rowsPerPage, (pageIdx + 1) * _rowsPerPage)
            )
            .toList()
        )
      )
    )
  );

  const page$ = useDataStream<number>(
    0,
    mergeTyped(
      pageChange$,
      rowsPerPage$.pipe(distinctUntilChanged(), mapTo(0)),
      projectListFiltered$.pipe(
        map(a => a.value.latest.size),
        distinctUntilChanged(),
        mapTo(0)
      )
    ).pipe(startWith(0))
  );

  const projectListFilteredCurrentPage$ = useDataStream<
    ListWithPromiseState<IProjectType>
  >(
    listWithPromiseStateInit<IProjectType>(),
    combineLatestTyped(
      projectListPaginated$,
      page$.pipe(debounceTime(paginationDebounceTime))
    ).pipe(
      map(([listlist, _page]) =>
        mapListLatest(listlist, ll => ll.get(_page) ?? IList<IProjectType>())
      )
    )
  );

  const authorOptions$ = useDataStream<{ id: string; label: string }[]>(
    [],
    projectList$.pipe(
      map(list =>
        list.value.latest
          .map(a => a.author)
          .toSet() // uniq
          .remove('')
          .toArray()
          .map(a => ({ id: a, label: a }))
      )
    )
  );

  const approverOptions$ = useDataStream<{ id: string; label: string }[]>(
    [],
    projectList$.pipe(
      map(list =>
        list.value.latest
          .map(a => a.approver)
          .toSet() // uniq
          .remove('')
          .toArray()
          .map(a => ({ id: a, label: a }))
      )
    )
  );

  const authorOptions = useStreamValue(authorOptions$, []);
  const approverOptions = useStreamValue(approverOptions$, []);

  const page = useStreamValue(page$, 0);

  const projectListFiltered = useStreamValue(projectListFiltered$);
  const numRows = projectListFiltered?.value.latest.size ?? 0;

  const projectListFilteredCurrentPage = useStreamValue(
    projectListFilteredCurrentPage$
  );

  return {
    searchString,
    onSearchStringChange,
    status,
    setStatus,
    author,
    setAuthor,
    approver,
    setApprover,
    page,
    onPageChange,
    rowsPerPage,
    setRowsPerPage,
    numRows,
    authorOptions,
    approverOptions,
    projectListFilteredCurrentPage
  };
};
